import { Select, TextInput, TextArea } from "@sede-enterprise/shell-ds-react-framework";
import { Dispatch, SetStateAction, ReactElement } from "react";
import { IN_SCOPE, COUNTRY_NAME, NOT_UPDATED, OUT_OF_SCOPE, COUNTRY_CODE, ENDUR_SEAM_REGION_SCOPE_TABLE, NUCLEUS_SEAM_REGION_SCOPE_TABLE, ENDURSLMT_STATIC_MAP_NCF_TABLE, ENDURSLMT_I_POWER_TABLE } from "../../../api/constants";
import { IEditFeatureDataState } from "../../../Reducers/InitialStates/EditFeatureInitialState";
import { OptionI, ICountry, IColumnRender, Action, IEditTableData, IAuditTableData } from "../../../Types/types";
import { ColumnIdentifiers, ConfigOptions, OptionLists, X } from "../../../Types/types2";
import { isQtyValid } from "../../pages/ViewEditReportPage/ViewEditReportFunctions";
import { areStringsEqual, decorateColName, isCommentsError } from "../../Utils/utils";
import tableStyles from '../../style.module.css';
import '../tableHeadingStyle.css';
import tableEditOptions from "../../../api/TableEditOptions";
import { ColumnsType } from "@sede-enterprise/shell-ds-react-framework/build/esm/components/DeprecatedTable/Table.types";
import { handleEditData } from "./editAndAuditReportTableColsUtil1";

// check if corresponding row has any changes for allowed editable columns
export const isCommentsColDirty = (
  row: IEditTableData,
  state: IEditFeatureDataState,
  isEditError: boolean,
  ID1: string,
  COMMENTS1: string,
) => {
  const dirtydata = [...state.dirtyData];
  const masterComments: string = getMasterComments(
    isEditError,
    state,
    ID1,
    row,
    COMMENTS1,
  ) as string;
  // iterate the dirty data and check if current row ID is present in it and if present
  //  then if the comments are not updated, then return true. Coz comment is mandatory for any changed row
  for (const dirtData of dirtydata) {
    const rowComment = row[COMMENTS1 as keyof typeof row] ?? '';
    if (
      dirtData[ID1 as keyof typeof dirtData] === row[ID1 as keyof typeof row] &&
      isCommentsError(masterComments, rowComment.toString())
    ) {
      return true;
    }
  }
  return false;
};

export const getMasterComments = (
  isEditError: boolean,
  state: IEditFeatureDataState,
  ID1: string | number,
  row: IEditTableData,
  COMMENTS1: string | number,
) => {
  const dataSource = isEditError ? state.masterErroredRows : state.masterPaginationData;
  for (const data of dataSource) {
    if (data[ID1] === row[ID1]) {
      return data[COMMENTS1];
    }
  }
  return '';
};

export const isScopeTableCountryNameEmptyWhenInScope = (row: IEditTableData, SCOPE1: string) => {
  let isDirty = false;
  if ((row[SCOPE1] === IN_SCOPE || row[SCOPE1] === 1) && row[COUNTRY_NAME] === '') {
    isDirty = true;
  }
  return isDirty;
};

//  check if the particular box is dirty(different from master data)
export const isBoxDirty = (
  row: IEditTableData,
  value: string | number,
  val: string,
  isReadyToPublish: boolean,
  state: IEditFeatureDataState,
  booleanVars: { QTY1: string; DEAL_QUANTITY1: string; ID1: string },
  isEditError: boolean,
) => {
  const { QTY1, DEAL_QUANTITY1, ID1 } = booleanVars;
  const masterData = isReadyToPublish
    ? [...state.masterErroredRows]
    : [...state.masterPaginationData];

  const columnName = val;

  if ((areStringsEqual(val, QTY1) || areStringsEqual(val, DEAL_QUANTITY1)) && !isQtyValid(value)) {
    return false;
  }

  const dataToCheck = isEditError ? state.masterErroredRows : masterData;

  for (const checkData of dataToCheck) {
    if (
      checkData[ID1] === row[ID1] &&
      columnName.toLowerCase().includes('scope') &&
      Number(checkData[columnName]) === Number(value.toString().charAt(0))
    ) {
      return false;
    }
    if (checkData[ID1] === row[ID1] && checkData[columnName] === value) {
      return false;
    }
  }

  return true;
};

export const customOptionsFunc = (
  row: IEditTableData,
  columnName: string,
  colNames: { MARKET1: string; NCF_CATEGORY1: string; ID1: string },
  state: IEditFeatureDataState,
  booleanVars: { isAsiaJapanTableSelected: boolean; isEditError: boolean },
  options: { ncfCatOptionsForAsiaJapanTable: OptionI[]; countryOptionsAmericas: OptionI[] },
) => {
  let customOptions = [];
  const { isAsiaJapanTableSelected, isEditError } = booleanVars;
  const { ncfCatOptionsForAsiaJapanTable, countryOptionsAmericas } = options;
  const { MARKET1, NCF_CATEGORY1, ID1 } = colNames;

  if (columnName === MARKET1) {
    customOptions = JSON.parse(JSON.stringify(state.marketOptions));
  } else if (columnName === NCF_CATEGORY1) {
    if (isAsiaJapanTableSelected) {
      customOptions = JSON.parse(JSON.stringify(ncfCatOptionsForAsiaJapanTable));
    } else {
      customOptions = JSON.parse(JSON.stringify(state.ncfCategoryOptions));
    }
  } else if (columnName === COUNTRY_NAME) {
    customOptions = JSON.parse(JSON.stringify(countryOptionsAmericas));
  }

  let masterData = [];
  let masterColValue = '';
  if (isEditError) {
    masterData = [...state.masterErroredRows];
  } else {
    masterData = [...state.masterPaginationData];
  }

  for (const masterDataObj of masterData) {
    if (masterDataObj[ID1] === row[ID1]) {
      masterColValue = masterDataObj[columnName] as string;
    }
  }

  for (const optionData of customOptions) {
    if (optionData['label']?.toString().toUpperCase() === masterColValue?.toUpperCase()) {
      optionData['className'] = tableStyles.masterOption;
    }
  }

  return customOptions;
};

export const updateColorHelper = (
  columnName: string,
  row: IEditTableData,
  isEditError: boolean,
  state: IEditFeatureDataState,
  COMMENTS1: string,
  setStateFuncs: {
    setUpdatedCol: Dispatch<SetStateAction<string>>;
    setOriginalVal: Dispatch<SetStateAction<string | number>>;
    setUpdatedVal: Dispatch<SetStateAction<string | number>>;
  },
  ID1: string | number,
) => {
  const { setUpdatedCol, setOriginalVal, setUpdatedVal } = setStateFuncs;
  let masterData: IEditTableData[] = [];
  let masterVal: string | number = '';
  if (isEditError) {
    masterData = [...state.masterErroredRows];
  } else {
    masterData = [...state.masterPaginationData];
  }
  //  for showing helpers to user
  if (!areStringsEqual(columnName, COMMENTS1)) {
    setUpdatedCol(columnName);
    for (const masterDataObj of masterData) {
      if (masterDataObj[ID1] === row[ID1]) {
        masterVal = masterDataObj[columnName];
      }
    }
    setOriginalVal(masterVal);
    if (masterVal === row[columnName]) {
      setUpdatedVal(NOT_UPDATED);
    } else {
      setUpdatedVal(row[columnName]);
    }
  }
};

export const setCountryCodeValue = (
  row: IEditTableData,
  SCOPE1: string,
  countryNameAndCodeAmericas: ICountry[],
) => {
  const countryName = row[COUNTRY_NAME];
  let countryCode = '';
  const scope = row[SCOPE1];

  if (scope === OUT_OF_SCOPE) {
    countryCode = countryNameAndCodeAmericas[0].COUNTRY_CODE;
  } else {
    for (const ind of countryNameAndCodeAmericas) {
      if (ind.COUNTRY_NAME === countryName) {
        countryCode = ind.COUNTRY_CODE;
      }
    }
  }

  return countryCode;
};

export const clearHelper = (
  setUpdatedCol: Dispatch<SetStateAction<string>>,
  setOriginalVal: Dispatch<SetStateAction<string | number>>,
  setUpdatedVal: Dispatch<SetStateAction<string | number>>,
) => {
  setUpdatedCol('');
  setOriginalVal('');
  setUpdatedVal('');
};

export const updateCols = (
  colData: { cols: string[]; colObj: IColumnRender[]; colNames: string[] },
  booleanOptions: {
    isEditable: boolean;
    isReadyToPublish: boolean;
    isEditError: boolean;
    isAsiaJapanTableSelected: boolean;
    isScopeTableSelected: boolean;
  },
  setFuncs: {
    setUpdatedCol: Dispatch<SetStateAction<string>>;
    setOriginalVal: Dispatch<SetStateAction<string | number>>;
    setUpdatedVal: Dispatch<SetStateAction<string | number>>;
    setIsQtyExceedThreeWholeDigits: Dispatch<SetStateAction<boolean>>;
    setIsDirtyData: Dispatch<SetStateAction<boolean>>;
  },
  optionsData: {
    countryNameAndCodeAmericas: ICountry[];
    scopeOptions: OptionI[];
    ncfCatOptionsForAsiaJapanTable: OptionI[];
    countryOptionsAmericas: OptionI[];
  },
  actions: { state: IEditFeatureDataState; dispatch: Dispatch<Action> },
  errorTableData: IEditTableData[],
) => {
  const { cols, colObj, colNames } = colData;
  const {
    isEditable,
    isReadyToPublish,
    isEditError,
    isAsiaJapanTableSelected,
    isScopeTableSelected,
  } = booleanOptions;
  const {
    setUpdatedCol,
    setOriginalVal,
    setUpdatedVal,
    setIsQtyExceedThreeWholeDigits,
    setIsDirtyData,
  } = setFuncs;
  const {
    countryNameAndCodeAmericas,
    scopeOptions,
    ncfCatOptionsForAsiaJapanTable,
    countryOptionsAmericas,
  } = optionsData;
  const { state, dispatch } = actions;

  const setStateFuncs = { setUpdatedCol, setOriginalVal, setUpdatedVal };
  const booleanVar = { isEditError, isScopeTableSelected };
  const stateVars = {
    state,
    setIsDirtyData,
    dispatch,
    setUpdatedVal,
    setOriginalVal,
    setUpdatedCol,
    countryNameAndCodeAmericas,
    setIsQtyExceedThreeWholeDigits,
    errorTableData,
  };

  const [
    ID1,
    QTY1,
    DEAL_QUANTITY1,
    MARKET1,
    NCF_CATEGORY1,
    BUYSELL1,
    COMMENTS1,
    SCOPE1,
    VOLUME1,
    NCF_SCOPE1,
  ] = colNames;

  const createInputComponent = (props: {
    Component: React.ElementType;
    val: string;
    row: IEditTableData;
    value: string | number;
    index: number;
    disabled: boolean;
    ariaLabel: string | null;
    options: OptionI[] | null;
    type?: string;
    invalid?: boolean;
  }) => {
    const { Component, val, row, value, disabled, ariaLabel, options, type, invalid, index } =
      props;
    return (
      <Component
        onMouseEnter={() =>
          updateColorHelper(val, row, isEditError, state, COMMENTS1, setStateFuncs, ID1)
        }
        onMouseLeave={() => clearHelper(setUpdatedCol, setOriginalVal, setUpdatedVal)}
        value={value}
        style={
          isBoxDirty(
            row,
            value,
            val,
            isReadyToPublish,
            state,
            { QTY1, DEAL_QUANTITY1, ID1 },
            isEditError,
          )
            ? styles.colChanged
            : styles.nothing
        }
        disabled={disabled}
        size='small'
        aria-label={ariaLabel}
        allowClear={false}
        options={options}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
          handleEditData({ e, val, index, row }, booleanVar, colNames, stateVars)
        }
        type={type}
        invalid={invalid}
      />
    );
  };

  const columnIdentifiers: ColumnIdentifiers = {
    MARKET1,
    NCF_CATEGORY1,
    ID1,
    BUYSELL1,
    QTY1,
    DEAL_QUANTITY1,
    SCOPE1,
    VOLUME1,
    NCF_SCOPE1,
    COMMENTS1,
  };

  const configOptions: ConfigOptions = {
    createInputComponent,
    isReadyToPublish,
    state,
    isAsiaJapanTableSelected,
    isEditError,
  };

  const optionLists: OptionLists = {
    ncfCatOptionsForAsiaJapanTable,
    countryOptionsAmericas,
    countryNameAndCodeAmericas,
    scopeOptions,
  };

  const columnsConfig = getColConfig(columnIdentifiers, configOptions, optionLists);

  cols.forEach((val: string) => {
    if ((isEditable || isReadyToPublish) && state.editableCol.indexOf(val) > -1) {
      columnsConfig.forEach((config) => {
        if (config.condition(val)) {
          colObj.push({
            dataIndex: val,
            key: val,
            title: <div>{decorateColName(val)}</div>,
            width: 100,
            className: tableStyles.reportPublishTableCols,
            render: config.render(val),
          });
        }
      });
    } else if ((BUYSELL1 && areStringsEqual(val, BUYSELL1)) || val.toLowerCase() === 'region') {
      colObj.push({
        dataIndex: val,
        key: val,
        width: 100,
        title: <div>{decorateColName(val)}</div>,
        className: tableStyles.nonEditableCols,
        render: (value: string): ReactElement => <>{value.toUpperCase()}</>,
      });
    } else {
      colObj.push({
        dataIndex: val,
        key: val,
        title: <div>{decorateColName(val)}</div>,
        width: 50,
        className: tableStyles.nonEditableCols,
      });
    }
  });
  return colObj;
};

export const getColConfig = (
  columnIdentifiers: ColumnIdentifiers,
  configOptions: ConfigOptions,
  optionLists: OptionLists,
) => {
  const {
    MARKET1,
    NCF_CATEGORY1,
    ID1,
    BUYSELL1,
    QTY1,
    DEAL_QUANTITY1,
    SCOPE1,
    VOLUME1,
    NCF_SCOPE1,
    COMMENTS1,
  } = columnIdentifiers;

  const { createInputComponent, isReadyToPublish, state, isAsiaJapanTableSelected, isEditError } =
    configOptions;

  const {
    ncfCatOptionsForAsiaJapanTable,
    countryOptionsAmericas,
    countryNameAndCodeAmericas,
    scopeOptions,
  } = optionLists;
  return [
    {
      condition: (val: string) => areStringsEqual(val, MARKET1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement => {
            return createInputComponent({
              Component: Select,
              val,
              row,
              value,
              index,
              disabled: isReadyToPublish,
              ariaLabel: 'Select',
              options: customOptionsFunc(
                row,
                val,
                { MARKET1, NCF_CATEGORY1, ID1 },
                state,
                { isAsiaJapanTableSelected, isEditError },
                { ncfCatOptionsForAsiaJapanTable, countryOptionsAmericas },
              ),
            });
          },
    },
    {
      condition: (val: string) => areStringsEqual(val, NCF_CATEGORY1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: Select,
              val,
              row,
              value,
              index,
              disabled: isReadyToPublish,
              ariaLabel: 'Select',
              options: customOptionsFunc(
                row,
                val,
                { MARKET1, NCF_CATEGORY1, ID1 },
                state,
                { isAsiaJapanTableSelected, isEditError },
                { ncfCatOptionsForAsiaJapanTable, countryOptionsAmericas },
              ),
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, BUYSELL1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: Select,
              val,
              row,
              value,
              index,
              disabled: isReadyToPublish,
              ariaLabel: 'Select',
              options: state.buySellOptions,
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, QTY1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: TextInput,
              val,
              row,
              value,
              index,
              disabled: isReadyToPublish,
              type: 'number',
              ariaLabel: null,
              options: null,
              invalid: !isQtyValid(value),
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, DEAL_QUANTITY1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: TextInput,
              val,
              row,
              value,
              index,
              disabled: isReadyToPublish,
              type: 'number',
              ariaLabel: null,
              options: null,
              invalid: !isQtyValid(value),
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, VOLUME1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: TextInput,
              val,
              row,
              value: value === '' ? 0 : value,
              index,
              disabled: isReadyToPublish,
              type: 'number',
              ariaLabel: null,
              options: null,
              invalid: !isQtyValid(value === '' ? 0 : value),
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, COUNTRY_NAME),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: Select,
              val,
              row,
              value,
              index,
              disabled: isReadyToPublish || row[SCOPE1] === OUT_OF_SCOPE || row[SCOPE1] === 0,
              ariaLabel: 'Select',
              options: customOptionsFunc(
                row,
                val,
                { MARKET1, NCF_CATEGORY1, ID1 },
                state,
                { isAsiaJapanTableSelected, isEditError },
                { ncfCatOptionsForAsiaJapanTable, countryOptionsAmericas },
              ),
              invalid: isScopeTableCountryNameEmptyWhenInScope(row, SCOPE1),
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, COUNTRY_CODE),
      render:
        (val: string) =>
          (_value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: TextInput,
              val,
              row,
              value: setCountryCodeValue(row, SCOPE1, countryNameAndCodeAmericas),
              index,
              disabled: true,
              ariaLabel: null,
              options: null,
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, SCOPE1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: Select,
              val,
              row,
              value: value === 0 || value === OUT_OF_SCOPE ? OUT_OF_SCOPE : IN_SCOPE,
              index,
              disabled: isReadyToPublish,
              ariaLabel: 'Select',
              options: scopeOptions,
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, NCF_SCOPE1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: Select,
              val,
              row,
              value: value === 0 || value === OUT_OF_SCOPE || value === '0' ? OUT_OF_SCOPE : IN_SCOPE,
              index,
              disabled: isReadyToPublish,
              ariaLabel: 'Select',
              options: scopeOptions,
            }),
    },
    {
      condition: (val: string) => areStringsEqual(val, COMMENTS1),
      render:
        (val: string) =>
          (value: string | number, row: IEditTableData, index: number): ReactElement =>
            createInputComponent({
              Component: TextArea,
              val,
              row,
              value,
              index,
              disabled: isReadyToPublish,
              ariaLabel: null,
              options: null,
              invalid: isCommentsColDirty(row, state, isEditError, ID1, COMMENTS1),
            }),
    },
  ];
};

const styles = {
  colChanged: {
    border: '2px solid #8ef4b7',
  },
  nothing: {},
  countryCode: {
    color: 'black',
  },
  countryCodeChanged: {
    border: '2px solid #8ef4b7',
    color: 'black',
  },
};

export const editAndAuditReportTableCols = (
  cols: string[],
  state: IEditFeatureDataState,
  dispatch: Dispatch<Action>,
  ...otherArgs: X | []
) => {
  const [
    currColumns,
    isEditable,
    isReadyToPublish,
    isEditError,
    setIsDirtyData,
    errorTableData,
    setIsQtyExceedThreeWholeDigits,
    setUpdatedCol,
    setOriginalVal,
    setUpdatedVal,
    regionOptionsForAmericasSeamRegionTable,
    ncfCatOptionsForAsiaJapanTable,
  ] = otherArgs as X;

  let colObj: IColumnRender[] = [];

  const scopeOptions = tableEditOptions.scopeOptions;
  const countryOptionsAmericas: OptionI[] = [];
  let countryNameAndCodeAmericas: ICountry[] = [{ COUNTRY_NAME: 'SELECT', COUNTRY_CODE: 'SELECT' }];
  if (regionOptionsForAmericasSeamRegionTable) {
    countryNameAndCodeAmericas = [...regionOptionsForAmericasSeamRegionTable];
    for (const nameCode of countryNameAndCodeAmericas) {
      countryOptionsAmericas.push({
        value: nameCode.COUNTRY_NAME,
        label: nameCode.COUNTRY_NAME,
      });
    }
  }

  const colNames = currColumns?.slice(0) || [];

  const isScopeTableSelected =
    state.tableSelected === ENDUR_SEAM_REGION_SCOPE_TABLE ||
    state.tableSelected === NUCLEUS_SEAM_REGION_SCOPE_TABLE;

  const isAsiaJapanTableSelected =
    state.tableSelected === ENDURSLMT_STATIC_MAP_NCF_TABLE ||
    state.tableSelected === ENDURSLMT_I_POWER_TABLE;

  const colData = { cols, colObj, colNames };
  const booleanOptions = {
    isEditable,
    isReadyToPublish,
    isEditError,
    isAsiaJapanTableSelected,
    isScopeTableSelected,
  };
  const setFuncs = {
    setUpdatedCol,
    setOriginalVal,
    setUpdatedVal,
    setIsQtyExceedThreeWholeDigits,
    setIsDirtyData,
  };
  const optionsData = {
    countryNameAndCodeAmericas,
    scopeOptions,
    ncfCatOptionsForAsiaJapanTable,
    countryOptionsAmericas,
  };
  const actions = { state, dispatch };
  colObj = updateCols(colData, booleanOptions, setFuncs, optionsData, actions, errorTableData);
  //  create/re-create columns

  return colObj as unknown as ColumnsType<IAuditTableData | IEditTableData>;
};